/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import dayjs from "dayjs";
import { dateFormat } from "~/constants";
import { generateNumericArray, sum } from "~/tools";
import { splitToQuarter } from "../helpers";

type Props = {
  monthly: number[];
  newBudget: number[][];
  growthRate: number;
  yearlyBudget: number;
};

export function useBudgetBreakdown({
  monthly,
  newBudget,
  growthRate,
  yearlyBudget,
}: Props) {
  const startOfYear = dayjs().startOf("year");

  const quarters = React.useMemo(() => {
    const months = generateNumericArray(12).map((_, i) =>
      startOfYear.add(i, "month").format(dateFormat.monthShort)
    );
    return splitToQuarter(months);
  }, [startOfYear]);

  const quarterlyBudgets = React.useMemo(
    () => newBudget.map((quarter) => sum(quarter)),
    [newBudget]
  );

  const hasAnyChanged = React.useMemo(
    () =>
      newBudget.flat().some((value, index) => {
        return monthly[index] !== value;
      }),
    [newBudget, monthly]
  );

  const growthRateBudgetCoefficient = React.useMemo(
    () => Math.max(1 + growthRate / 100, 0),
    [growthRate]
  );

  const growthRateBudget = React.useMemo(
    () => yearlyBudget * growthRateBudgetCoefficient,
    [yearlyBudget, growthRateBudgetCoefficient]
  );

  return {
    quarters,
    quarterlyBudgets,
    hasAnyChanged,
    growthRateBudget,
    growthRateBudgetCoefficient,
  };
}
